import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import sanitizeHtml from 'sanitize-html';
import { useDispatch } from 'react-redux';
import apis from '@src/apis';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import CommonInput from '@src/new-components/Input/CommonInput';
import CommonButton from '@src/new-components/Button';
import Typography from '@src/new-components/Typography';
import { COLOR } from '@src/styles/color';
import {
  MAX_LENGTH_VOICE_NAME,
  MIN_LENGTH_USERNAME,
  VOICE_NAME_REGEX,
} from '@src/constants/voiceCloning';
import actions from '@src/redux/actions';
import { ERROR_CODE } from '@src/errors/code';
import {
  StyledDuplicateVoiceDialog,
  StyledDuplicateVoiceDialogAction,
} from './index.style';

const DuplicateVoiceDialog = ({ open, onClose, duplicatingVoice }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [voiceName, setVoiceName] = useState('');
  const [voiceNameError, setVoiceNameError] = useState('');
  const [loading, setLoading] = useState(false);

  const isDisabledButton = loading || Boolean(voiceNameError);

  const handleClose = () => {
    setVoiceName('');
    setVoiceNameError('');
    onClose();
  };

  const validateVoiceName = (name) => {
    let error;
    const normalizedVoiceName = name?.normalize('NFC') ?? '';
    if (!normalizedVoiceName) {
      error = t('requiredField', { field: t('voiceName') });
    } else if (normalizedVoiceName.length < MIN_LENGTH_USERNAME) {
      error = t('voiceNameDescription');
    } else if (normalizedVoiceName.length > MAX_LENGTH_VOICE_NAME) {
      error = t('voiceNameDescription');
    } else if (!VOICE_NAME_REGEX.test(normalizedVoiceName)) {
      error = t('voiceNameDescription');
    }

    return error;
  };

  const handleChangeVoiceName = (value) => {
    const error = validateVoiceName(value);
    setVoiceNameError(error);
    setVoiceName(value);
  };

  const renderEndAdornment = () => (
    <Typography variant="body-14-regular" color={COLOR.label2[48]}>
      {voiceName?.length ?? 0}/{MAX_LENGTH_VOICE_NAME}
    </Typography>
  );

  const handleDuplicateVoice = async () => {
    setLoading(true);

    const error = validateVoiceName(voiceName);
    if (error) {
      setVoiceNameError(error);
      setLoading(false);
      return;
    }

    const response = await apis.voiceCloning.duplicateVoice({
      voiceId: duplicatingVoice.id,
      name: voiceName,
    });

    if (response.status) {
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: 'duplicateVoiceSuccess',
        }),
      );
      handleClose();
    }

    if (!response.status) {
      const { errorCode } = response;

      switch (errorCode) {
        case ERROR_CODE.VOICE_NAME_EXISTS:
          setVoiceNameError(t('voiceNameExistsError'));
          break;
        case ERROR_CODE.VOICE_NAME_INVALID:
          setVoiceNameError(t('voiceNameDescription'));
          break;
        default:
          dispatch(
            actions.noti.push({
              severity: 'error',
              message: 'errorOccurred',
            }),
          );
          break;
      }
    }

    setLoading(false);
  };

  const renderContent = () => (
    <StyledDuplicateVoiceDialog>
      <div className="input-voice-name">
        <Typography variant="body-14-medium" color={COLOR.label2[100]}>
          {t('voiceName')}
        </Typography>
        <CommonInput
          placeholder={t('enterDuplicateVoiceName')}
          name="voiceName"
          value={voiceName}
          inputProps={{ maxLength: MAX_LENGTH_VOICE_NAME }}
          onChange={(e) => handleChangeVoiceName(e.target.value)}
          error={Boolean(voiceNameError)}
          helperText={voiceNameError}
          endAdornment={renderEndAdornment()}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              padding: '0px 12px 0px 0px',
              height: '40px',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '20px',
              letterSpacing: '-0.4px',
            },
          }}
        />
      </div>
      <div className="content-container">
        <div className="content-item-container">
          <AccessTimeRoundedIcon
            sx={{ color: COLOR.navyBlue, fontSize: '20px' }}
          />
          <div className="content-item">
            <Typography variant="body-14-bold" color={COLOR.label2[100]}>
              {t('saveRecordingTime')}
            </Typography>
            <Typography
              variant="body-12-regular"
              color={COLOR.label2[64]}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(
                  t('saveRecordingTimeDescription', {
                    voiceName: duplicatingVoice?.name,
                  }),
                  {
                    allowedTags: ['span'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          </div>
        </div>

        <div className="content-item-container">
          <AutoAwesomeRoundedIcon
            sx={{ color: COLOR.navyBlue, fontSize: '20px' }}
          />
          <div className="content-item">
            <Typography variant="body-14-bold" color={COLOR.label2[100]}>
              {t('useMoreFeatures')}
            </Typography>
            <Typography
              variant="body-12-regular"
              color={COLOR.label2[64]}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(t('useMoreFeaturesDescription'), {
                  allowedTags: ['span'],
                  allowedAttributes: {
                    span: ['style'],
                  },
                }),
              }}
            />
          </div>
        </div>
      </div>
    </StyledDuplicateVoiceDialog>
  );

  const renderAction = () => (
    <StyledDuplicateVoiceDialogAction>
      <CommonButton
        variant="outlined"
        onClick={handleClose}
        disabled={loading}
        customStyle={{
          height: '40px',
          padding: '8px 24px',
        }}
      >
        <Typography variant="body-14-semibold" color={COLOR.label2[100]}>
          {t('cancel')}
        </Typography>
      </CommonButton>
      <CommonButton
        variant="contained"
        onClick={handleDuplicateVoice}
        disabled={isDisabledButton}
        customStyle={{
          height: '40px',
          padding: '8px 24px',
        }}
      >
        <Typography variant="body-14-semibold" color={COLOR.label2[100]}>
          {t('createVoice')}
        </Typography>
      </CommonButton>
    </StyledDuplicateVoiceDialogAction>
  );

  return (
    <CommonDialog
      width="512px"
      open={open}
      onClose={handleClose}
      disableAutoClose
      title={t('duplicateVoiceDialogTitle')}
      contentComponent={renderContent()}
      actionComponent={renderAction()}
    />
  );
};

export default DuplicateVoiceDialog;
